import { AccountSecurityStatusResponseSchema, RegisterTotpRequestSchema, RegisterTotpResponseSchema, VerifyTotpRequestSchema, VerifyTotpResponseSchema, RemoveTotpRequestSchema, RemoveTotpResponseSchema, CreatePasskeyRegistrationLinkRequestSchema, CreatePasskeyRegistrationLinkResponseSchema, RemovePasskeyRequestSchema, RemovePasskeyResponseSchema } from './account-security.schema';
const SENSITIVE_OPERATION_403 = 'Re-authentication required (AUTH_REAUTH_REQUIRED) or no active membership (AUTH_NO_MEMBERSHIP)';
/**
 * Account Security Status Endpoint Contract
 * Tells the Security tab which self-service capabilities apply to the
 * authenticated user (password change availability, TOTP state).
 */ export const accountSecurityStatusContract = {
    method: 'GET',
    path: '/api/v2/profile/account-security',
    description: 'Returns password-change availability and TOTP status for the authenticated user',
    responseSchema: AccountSecurityStatusResponseSchema,
    statusCodes: {
        success: 200,
        errors: {
            401: 'Unauthorized - invalid or missing token',
            503: 'Identity provider unreachable'
        }
    }
};
/**
 * Register TOTP Endpoint Contract
 * Starts a TOTP registration in Zitadel; the returned otpauth:// URI and
 * secret feed the QR/manual setup step, verified separately.
 */ export const registerTotpContract = {
    method: 'POST',
    path: '/api/v2/profile/totp/register',
    description: 'Starts TOTP registration, returns otpauth URI and secret',
    requestSchema: RegisterTotpRequestSchema,
    responseSchema: RegisterTotpResponseSchema,
    statusCodes: {
        success: 200,
        errors: {
            400: 'TOTP unavailable, not Zitadel-linked, or already configured',
            401: 'Unauthorized - invalid or missing token',
            403: SENSITIVE_OPERATION_403,
            503: 'Identity provider unreachable'
        }
    }
};
/**
 * Verify TOTP Endpoint Contract
 * Confirms the pending TOTP registration with a code from the
 * authenticator app.
 */ export const verifyTotpContract = {
    method: 'POST',
    path: '/api/v2/profile/totp/verify',
    description: 'Verifies the pending TOTP registration with a 6-digit code',
    requestSchema: VerifyTotpRequestSchema,
    responseSchema: VerifyTotpResponseSchema,
    statusCodes: {
        success: 200,
        errors: {
            400: 'Invalid code, no pending registration, or TOTP unavailable',
            401: 'Unauthorized - invalid or missing token',
            403: SENSITIVE_OPERATION_403,
            503: 'Identity provider unreachable'
        }
    }
};
/**
 * Remove TOTP Endpoint Contract
 * Removes the configured TOTP factor (idempotent: an already-removed
 * factor still answers success).
 */ export const removeTotpContract = {
    method: 'DELETE',
    path: '/api/v2/profile/totp',
    description: 'Removes the configured TOTP factor',
    requestSchema: RemoveTotpRequestSchema,
    responseSchema: RemoveTotpResponseSchema,
    statusCodes: {
        success: 200,
        errors: {
            400: 'TOTP unavailable or not Zitadel-linked',
            401: 'Unauthorized - invalid or missing token',
            403: SENSITIVE_OPERATION_403,
            503: 'Identity provider unreachable'
        }
    }
};
/**
 * Create Passkey Registration Link Endpoint Contract
 * Mints a one-time registration code in Zitadel and returns a deep link
 * into the hosted Login V2 passkey-setup page. The credential is created
 * there (auth origin) so its WebAuthn RP ID matches the login domain.
 */ export const createPasskeyRegistrationLinkContract = {
    method: 'POST',
    path: '/api/v2/profile/passkeys/registration-link',
    description: 'Creates a one-time passkey registration link into the hosted login page',
    requestSchema: CreatePasskeyRegistrationLinkRequestSchema,
    responseSchema: CreatePasskeyRegistrationLinkResponseSchema,
    statusCodes: {
        success: 200,
        errors: {
            400: 'Passkeys unavailable or not Zitadel-linked',
            401: 'Unauthorized - invalid or missing token',
            403: SENSITIVE_OPERATION_403,
            503: 'Identity provider unreachable'
        }
    }
};
/**
 * Remove Passkey Endpoint Contract
 * Removes one registered passkey. Idempotent for a passkey that is already
 * gone (still answers success), but NOT unconditional: removing the last
 * remaining primary sign-in method is refused with 409.
 */ export const removePasskeyContract = {
    method: 'DELETE',
    path: '/api/v2/profile/passkeys/:passkeyId',
    description: 'Removes a registered passkey',
    requestSchema: RemovePasskeyRequestSchema,
    responseSchema: RemovePasskeyResponseSchema,
    statusCodes: {
        success: 200,
        errors: {
            400: 'Passkeys unavailable or not Zitadel-linked',
            401: 'Unauthorized - invalid or missing token',
            403: SENSITIVE_OPERATION_403,
            409: 'Conflict - removing the last remaining sign-in method',
            503: 'Identity provider unreachable'
        }
    }
};

//# sourceMappingURL=account-security.contract.js.map